export const FRONT_DOOR_SCHEMA_VERSION = 1;

const HOST_ENTRY_TYPES = ['Host', 'Head Host', 'Ambassador'];
const FREE_ACTION_KINDS = ['namedFreeCheckin', 'genericFree'];

export const NEUTRAL_FRONT_DOOR_RULES = {
  schemaVersion: FRONT_DOOR_SCHEMA_VERSION,
  timezone: 'Australia/Melbourne',
  sessionRolloverHour: 6,
  standardRulesEnabled: false,
  shadowMode: false,
  shadowActingSource: 'legacy',
  reminders: {
    elevenPm: { enabled: false, time: '23:00', lateWindowMinutes: 60 },
    midnight: { enabled: false, time: '00:00', lateWindowMinutes: 60 }
  },
  guards: {
    birthdayOfferEnded: {
      enabled: false,
      mode: 'override',
      actionKinds: ['namedFreeCheckin', 'perNameFreeDelta'],
      appliesToTypes: ['Birthdays'],
      window: { start: '23:00', end: '06:00' },
      allowDoorCutoffOverride: false
    },
    yoursTrulyClosedFreeList: {
      enabled: false,
      mode: 'block',
      actionKinds: FREE_ACTION_KINDS,
      appliesToTypes: HOST_ENTRY_TYPES,
      appliesToTeams: ['Yours Truly'],
      window: { start: '00:00', end: '06:00' },
      allowDoorCutoffOverride: false
    },
    hostBenefitCutoff: {
      enabled: false,
      mode: 'override',
      actionKinds: ['benefitDone'],
      appliesToTypes: HOST_ENTRY_TYPES,
      window: { start: '23:00', end: '06:00' },
      allowDoorCutoffOverride: false
    },
    hostFreeEntryCutoff: {
      enabled: false,
      mode: 'override',
      actionKinds: FREE_ACTION_KINDS,
      appliesToTypes: HOST_ENTRY_TYPES,
      excludeTeams: ['Yours Truly'],
      window: { start: '23:00', end: '06:00' },
      allowDoorCutoffOverride: true
    }
  },
  perListCustomCutoff: {
    enabled: true,
    mode: 'override',
    actionKinds: FREE_ACTION_KINDS,
    allowDoorCutoffOverride: true
  },
  birthdayBenefits: { enabled: false, freeCap: 0 },
  discountPricing: { enabled: false, cutoffTime: '', teamCutoffs: {} }
};

export const CLUB_ASTRA_FRONT_DOOR_RULES = mergeDeep(NEUTRAL_FRONT_DOOR_RULES, {
  standardRulesEnabled: true,
  reminders: {
    elevenPm: { enabled: true },
    midnight: { enabled: true }
  },
  guards: {
    birthdayOfferEnded: { enabled: true },
    yoursTrulyClosedFreeList: { enabled: true },
    hostBenefitCutoff: { enabled: true },
    hostFreeEntryCutoff: { enabled: true }
  },
  birthdayBenefits: { enabled: true, freeCap: 5 },
  discountPricing: { enabled: true, cutoffTime: '23:00', teamCutoffs: { 'Yours Truly': '00:00' } }
});

export const NEUTRAL_FRONT_DOOR_COPY = {
  schemaVersion: FRONT_DOOR_SCHEMA_VERSION,
  reminders: {
    elevenPm: { title: '11PM Reminder', message: '', bullets: [] },
    midnight: { title: '12AM Reminder', message: '', bullets: [] }
  },
  guards: {
    birthdayOfferEnded: {
      title: 'Birthday Offer Ended',
      message: 'It is past {cutoffLabel} - the birthday offer has ended.',
      bullets: [],
      approveLabel: 'OVERRIDE',
      declineLabel: 'CANCEL'
    },
    yoursTrulyClosedFreeList: {
      title: 'Free List Closed',
      message: 'It is past {cutoffLabel} - this free list has ended.',
      bullets: []
    },
    hostBenefitCutoff: {
      title: 'Cut-Off Time Reached',
      message: 'It is past {cutoffLabel} - host benefits are past cut-off.',
      bullets: [],
      approveLabel: 'OVERRIDE',
      declineLabel: 'CANCEL'
    },
    hostFreeEntryCutoff: {
      title: 'Cut-Off Time Reached',
      message: 'It is past {cutoffLabel} - free entries for this list are past cut-off.',
      bullets: [],
      approveLabel: 'OVERRIDE',
      declineLabel: 'CANCEL'
    }
  },
  perListCustomCutoff: {
    title: '⏰ Cut-Off Time Reached',
    message: "It's past {cutoffLabel} — free entries for this list are no longer available.",
    bullets: [],
    approveLabel: 'OVERRIDE',
    declineLabel: 'CANCEL'
  },
  birthdayBenefits: { title: 'Benefits for Entire List before 11pm', items: [] },
  discountPricing: {
    title: 'Pricing Rule',
    heading: 'Guestlist pricing ends at {discountPricingCutoffLabel}',
    body: 'After {discountPricingCutoffLabel}, mark off the name and charge General Entry price.'
  }
};

export const CLUB_ASTRA_FRONT_DOOR_COPY = mergeDeep(NEUTRAL_FRONT_DOOR_COPY, {
  reminders: {
    elevenPm: {
      title: '⏰ 11PM Reminder',
      message:
        "It's now 11:00 PM\n" +
        '❌ Complimentary Birthday Package has ended.\n' +
        '❌ Drink cards and extra free entries for Hosts & Ambassadors are no longer available.\n' +
        '❌ Guestlist pricing has closed. Charge general entry prices for rest of the night.\n' +
        '✅ Continue to mark down guestlist payers on tablet for Host performance tracking.'
    },
    midnight: {
      title: '⏰ 12AM Reminder',
      message:
        "It's now 12:00 AM\n" +
        "❌ Yours Truly Host's Closed Free List entries have ended\n\n" +
        'Still Valid:\n' +
        '✅ Free Entry for Host\n' +
        '✅ Free Entry for Birthday person only\n' +
        '✅ Names on #Yours Truly Free List\n' +
        '✅ Names on {brandFreeListName}\n' +
        '✅ Names on #Emerson Management Frees List'
    }
  },
  guards: {
    birthdayOfferEnded: {
      title: '🎉 Birthday Offer Ended',
      message: "It's past {cutoffLabel} — the Complimentary Birthday Offer has ended.",
      bullets: [
        '✅ Only the birthday person receives free entry.',
        '❌ No drink cards or free entries are available for extra guests.'
      ]
    },
    yoursTrulyClosedFreeList: {
      title: '⏰ Free List Closed',
      message: "It's past {cutoffLabel} — Yours Truly Closed Free List has ended.\n👉 Please charge entry for all remaining guests.",
      bullets: []
    },
    hostBenefitCutoff: {
      title: '⏰ Cut-Off Time Reached',
      message: "It's past {cutoffLabel} — drink cards and free entries for hosts & ambassadors are no longer available.",
      bullets: []
    },
    hostFreeEntryCutoff: {
      title: '⏰ Cut-Off Time Reached',
      message: "It's past {cutoffLabel} — drink cards and free entries for hosts & ambassadors are no longer available.",
      bullets: []
    }
  },
  birthdayBenefits: {
    title: 'Benefits for Entire List before 11pm',
    items: [
      'Free Entry for Bday Name + {birthdayFreeCap}',
      '$100 Drink Card (Arrive with 5+ guests)',
      'Bottle of Champagne + Booth* (Arrive with 10+ girls)'
    ]
  }
});

export function cloneFrontDoorValue(value) {
  if (value === undefined) return undefined;
  // NB: do not use `value || {}` — that coerces legitimate falsy primitives
  // (false, 0, '') into {}, which flips every disabled toggle back on.
  return JSON.parse(JSON.stringify(value));
}

export function getDefaultFrontDoorRulesForEvent(eventId) {
  return eventId === 'club-astra'
    ? cloneFrontDoorValue(CLUB_ASTRA_FRONT_DOOR_RULES)
    : cloneFrontDoorValue(NEUTRAL_FRONT_DOOR_RULES);
}

export function getDefaultFrontDoorCopyForEvent(eventId) {
  return eventId === 'club-astra'
    ? cloneFrontDoorValue(CLUB_ASTRA_FRONT_DOOR_COPY)
    : cloneFrontDoorValue(NEUTRAL_FRONT_DOOR_COPY);
}

export function normalizeFrontDoorRules(input, fallback = NEUTRAL_FRONT_DOOR_RULES) {
  if (input && typeof input === 'object' && input.schemaVersion && input.schemaVersion !== FRONT_DOOR_SCHEMA_VERSION) {
    return cloneFrontDoorValue(NEUTRAL_FRONT_DOOR_RULES);
  }
  const merged = mergeDeep(fallback, input && typeof input === 'object' ? input : {});
  merged.schemaVersion = FRONT_DOOR_SCHEMA_VERSION;
  merged.standardRulesEnabled = merged.standardRulesEnabled === true;
  merged.shadowMode = merged.shadowMode === true;
  merged.shadowActingSource = merged.shadowActingSource === 'resolver' ? 'resolver' : 'legacy';
  coerceEnabledFlags(merged.reminders);
  coerceEnabledFlags(merged.guards);
  if (merged.perListCustomCutoff && typeof merged.perListCustomCutoff === 'object') {
    merged.perListCustomCutoff.enabled = merged.perListCustomCutoff.enabled === true;
  }
  merged.birthdayBenefits = {
    enabled: merged.birthdayBenefits?.enabled === true,
    freeCap: clampInteger(merged.birthdayBenefits?.freeCap, 0, 50, 0)
  };
  merged.discountPricing = {
    enabled: merged.discountPricing?.enabled === true,
    cutoffTime: normalizeTime(merged.discountPricing?.cutoffTime) || '',
    teamCutoffs: normalizeTeamCutoffs(merged.discountPricing?.teamCutoffs)
  };
  return merged;
}

export function normalizeFrontDoorCopy(input, fallback = NEUTRAL_FRONT_DOOR_COPY) {
  if (input && typeof input === 'object' && input.schemaVersion && input.schemaVersion !== FRONT_DOOR_SCHEMA_VERSION) {
    return cloneFrontDoorValue(NEUTRAL_FRONT_DOOR_COPY);
  }
  const merged = mergeDeep(fallback, input && typeof input === 'object' ? input : {});
  merged.schemaVersion = FRONT_DOOR_SCHEMA_VERSION;
  return merged;
}

function coerceEnabledFlags(map) {
  if (!map || typeof map !== 'object') return;
  for (const entry of Object.values(map)) {
    if (entry && typeof entry === 'object') entry.enabled = entry.enabled === true;
  }
}

function normalizeTime(timeStr) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(timeStr || '').trim());
  if (!match) return '';
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return '';
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return '';
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function normalizeTeamCutoffs(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const out = {};
  for (const [team, time] of Object.entries(input)) {
    const teamKey = String(team || '').trim();
    const normalized = normalizeTime(time);
    if (teamKey && normalized) out[teamKey] = normalized;
  }
  return out;
}

function clampInteger(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  const i = Math.floor(n);
  if (i < min || i > max) return fallback;
  return i;
}

function mergeDeep(base, patch) {
  if (Array.isArray(base) || Array.isArray(patch)) {
    return cloneFrontDoorValue(patch === undefined ? base : patch);
  }
  const out = cloneFrontDoorValue(base || {});
  if (!patch || typeof patch !== 'object') return out;
  for (const [key, value] of Object.entries(patch)) {
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      out[key] &&
      typeof out[key] === 'object' &&
      !Array.isArray(out[key])
    ) {
      out[key] = mergeDeep(out[key], value);
    } else {
      out[key] = cloneFrontDoorValue(value);
    }
  }
  return out;
}
